import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, THead, TH, TR, TD } from '@/components/ui/table';
import PanelResult from './PanelResult';
import {
  CK_ROUTES,
  CK_PERMISSIONS,
  BranchRequisitionStatus,
  apiMessage,
  type BranchRequisition,
  type WarehouseRef,
} from '../centralKitchenApi';
import { requisitionStatusTone, formatDateTime } from '../lib';

interface BranchOption {
  id: string;
  name: string;
}
interface ItemOption {
  id: string;
  name: string;
  sku?: string | null;
}

/**
 * A branch asking the commissary for stock. `create`/`submit` belong to the
 * asking branch and `approve` to the commissary, so the action set a row
 * offers depends on which side of that line the actor is on — the same split
 * `RequisitionsController` enforces with two different permission keys.
 */
export default function RequisitionsPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { confirm, prompt } = useConfirm();

  const [status, setStatus] = useState<BranchRequisitionStatus | ''>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [itemId, setItemId] = useState('');
  const [qty, setQty] = useState('');
  const [approveTarget, setApproveTarget] = useState<BranchRequisition | null>(null);
  const [sourceWarehouseId, setSourceWarehouseId] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const listParams = useMemo(() => ({ ...(status ? { status } : {}), limit: 100 }), [status]);
  const requisitions = useApi<unknown>(can(CK_PERMISSIONS.requisitionsView) ? CK_ROUTES.requisitions : null, listParams);
  const { rows } = rowsOf<BranchRequisition>(requisitions.data);

  const branches = useApi<unknown>(createOpen && can('branches.view') ? '/branches' : null, { limit: 200 });
  const { rows: branchRows } = rowsOf<BranchOption>(branches.data);
  const items = useApi<unknown>(createOpen ? CK_ROUTES.items : null, { limit: 300 });
  const { rows: itemRows } = rowsOf<ItemOption>(items.data);
  const warehouses = useApi<unknown>(approveTarget ? CK_ROUTES.warehouses : null, { isActive: true, limit: 200 });
  const { rows: warehouseRows } = rowsOf<WarehouseRef>(warehouses.data);

  const create = async () => {
    if (!branchId || !itemId || !qty.trim()) return;
    setBusyId('create');
    try {
      await api.post(CK_ROUTES.requisitions, {
        branchId,
        lines: [{ inventoryItemId: itemId, requestedQuantity: qty.trim() }],
      });
      toast.success(t('centralKitchen.requisitions.created'));
      setCreateOpen(false);
      setBranchId('');
      setItemId('');
      setQty('');
      requisitions.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const submit = async (r: BranchRequisition) => {
    setBusyId(r.id);
    try {
      await api.post(CK_ROUTES.requisitionSubmit(r.id));
      toast.success(t('centralKitchen.requisitions.submitted'));
      requisitions.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const submitApprove = async () => {
    if (!approveTarget || !sourceWarehouseId) return;
    setBusyId(approveTarget.id);
    try {
      await api.post(CK_ROUTES.requisitionApprove(approveTarget.id), { sourceWarehouseId });
      toast.success(t('centralKitchen.requisitions.approved'));
      setApproveTarget(null);
      setSourceWarehouseId('');
      requisitions.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (r: BranchRequisition) => {
    const reason = await prompt({
      title: t('centralKitchen.requisitions.rejectTitle'),
      label: t('centralKitchen.requisitions.rejectReasonLabel'),
      confirmLabel: t('centralKitchen.requisitions.rejectConfirm'),
      tone: 'danger',
      validate: (v) => (v.trim().length < 2 ? t('centralKitchen.requisitions.rejectReasonRequired') : null),
    });
    if (reason == null) return;
    setBusyId(r.id);
    try {
      await api.post(CK_ROUTES.requisitionReject(r.id), { reason: reason.trim() });
      toast.success(t('centralKitchen.requisitions.rejected'));
      requisitions.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const fulfil = async (r: BranchRequisition) => {
    setBusyId(r.id);
    try {
      await api.post(CK_ROUTES.requisitionFulfil(r.id));
      toast.success(t('centralKitchen.requisitions.fulfilled'));
      requisitions.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (r: BranchRequisition) => {
    const ok = await confirm({
      title: t('centralKitchen.requisitions.cancelTitle', { number: r.number }),
      confirmLabel: t('common.confirm'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusyId(r.id);
    try {
      await api.post(CK_ROUTES.requisitionCancel(r.id), {});
      toast.success(t('centralKitchen.requisitions.cancelled'));
      requisitions.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Field label={t('centralKitchen.production.filterStatus')} controlId="ck-req-status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as BranchRequisitionStatus | '')}>
            <option value="">{t('centralKitchen.transfers.allStatuses')}</option>
            {Object.values(BranchRequisitionStatus).map((s) => (
              <option key={s} value={s}>
                {t(`centralKitchen.requisitionStatus.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={requisitions.reload}>
            <Icon as={RefreshCw} />
            {t('common.refresh')}
          </Button>
          {can(CK_PERMISSIONS.requisitionsCreate) && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Icon as={Plus} />
              {t('centralKitchen.requisitions.new')}
            </Button>
          )}
        </div>
      </div>

      <TableWrap>
        <PanelResult
          loading={requisitions.loading}
          error={requisitions.error}
          count={rows.length}
          onRetry={requisitions.reload}
          subject={t('centralKitchen.requisitions.subject')}
          filtered={!!status}
          onClearFilters={() => setStatus('')}
          emptyTitle={t('centralKitchen.requisitions.emptyTitle')}
          emptyHint={t('centralKitchen.requisitions.emptyHint')}
        >
          <Table>
            <THead>
              <TR>
                <TH>{t('centralKitchen.requisitions.columns.number')}</TH>
                <TH>{t('centralKitchen.requisitions.columns.status')}</TH>
                <TH>{t('centralKitchen.requisitions.columns.branch')}</TH>
                <TH>{t('centralKitchen.requisitions.columns.neededBy')}</TH>
                <TH />
              </TR>
            </THead>
            <tbody>
              {rows.map((r) => (
                <TR key={r.id}>
                  <TD className="font-mono text-xs">{r.number}</TD>
                  <TD>
                    <Badge tone={requisitionStatusTone(r.status)}>{t(`centralKitchen.requisitionStatus.${r.status}`)}</Badge>
                  </TD>
                  <TD>{r.branch?.name ?? r.branchId}</TD>
                  <TD className="text-xs text-ink-muted">{formatDateTime(r.neededBy, i18n.language)}</TD>
                  <TD>
                    <div className="flex justify-end gap-1">
                      {can(CK_PERMISSIONS.requisitionsCreate) && r.status === BranchRequisitionStatus.DRAFT && (
                        <Button size="sm" variant="outline" onClick={() => submit(r)} disabled={busyId === r.id}>
                          {t('centralKitchen.requisitions.submit')}
                        </Button>
                      )}
                      {can(CK_PERMISSIONS.requisitionsApprove) && r.status === BranchRequisitionStatus.SUBMITTED && (
                        <>
                          <Button size="sm" onClick={() => setApproveTarget(r)} disabled={busyId === r.id}>
                            {t('centralKitchen.requisitions.approve')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => reject(r)} disabled={busyId === r.id}>
                            {t('centralKitchen.requisitions.reject')}
                          </Button>
                        </>
                      )}
                      {can(CK_PERMISSIONS.requisitionsFulfil) && r.status === BranchRequisitionStatus.APPROVED && (
                        <Button size="sm" onClick={() => fulfil(r)} disabled={busyId === r.id}>
                          {t('centralKitchen.requisitions.fulfil')}
                        </Button>
                      )}
                      {can(CK_PERMISSIONS.requisitionsCancel) && r.status === BranchRequisitionStatus.DRAFT && (
                        <Button size="sm" variant="ghost" onClick={() => cancel(r)} disabled={busyId === r.id}>
                          {t('common.cancel')}
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </PanelResult>
      </TableWrap>

      {createOpen && (
        <Dialog
          open
          onClose={() => setCreateOpen(false)}
          title={t('centralKitchen.requisitions.new')}
          description={t('centralKitchen.requisitions.newDescription')}
          className="max-w-md"
          footer={
            <>
              <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={busyId === 'create'}>
                {t('common.cancel')}
              </Button>
              <Button onClick={create} disabled={busyId === 'create' || !branchId || !itemId || !qty.trim()}>
                {t('centralKitchen.requisitions.create')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Field label={t('centralKitchen.requisitions.columns.branch')} required controlId="ck-req-branch">
              {branchRows.length > 0 ? (
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">{t('centralKitchen.requisitions.chooseBranch')}</option>
                  {branchRows.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input value={branchId} onChange={(e) => setBranchId(e.target.value)} />
              )}
            </Field>
            <Field label={t('centralKitchen.transfers.columns.item')} required controlId="ck-req-item">
              <Select value={itemId} onChange={(e) => setItemId(e.target.value)}>
                <option value="">{t('centralKitchen.transfers.chooseItem')}</option>
                {itemRows.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.name} {it.sku ? `(${it.sku})` : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('centralKitchen.transfers.columns.quantity')} required controlId="ck-req-qty">
              <Input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      )}

      {approveTarget && (
        <Dialog
          open
          onClose={() => setApproveTarget(null)}
          title={t('centralKitchen.requisitions.approveTitle', { number: approveTarget.number })}
          description={t('centralKitchen.requisitions.approveDescription')}
          className="max-w-sm"
          footer={
            <>
              <Button variant="outline" onClick={() => setApproveTarget(null)} disabled={busyId === approveTarget.id}>
                {t('common.cancel')}
              </Button>
              <Button onClick={submitApprove} disabled={busyId === approveTarget.id || !sourceWarehouseId}>
                {t('centralKitchen.requisitions.approveConfirm')}
              </Button>
            </>
          }
        >
          <Field label={t('centralKitchen.requisitions.sourceWarehouse')} required controlId="ck-req-source">
            <Select value={sourceWarehouseId} onChange={(e) => setSourceWarehouseId(e.target.value)}>
              <option value="">{t('centralKitchen.transfers.chooseWarehouse')}</option>
              {warehouseRows.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} ({w.code})
                </option>
              ))}
            </Select>
          </Field>
        </Dialog>
      )}
    </div>
  );
}
